import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

export function Header({ settings }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [blogDropdownOpen, setBlogDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const servicesRef = useRef(null);
  const blogRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
    setBlogDropdownOpen(false);
  }, [location.pathname]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setServicesDropdownOpen(false);
      }
      if (blogRef.current && !blogRef.current.contains(e.target)) {
        setBlogDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const phone = settings?.phone || "82 82 10 10";
  const hours = settings?.hours || "Mandag - Lørdag: 9 - 18";

  return (
    <header className="sticky top-0 z-50 w-full transition-all duration-300">
      {/* Main Navbar */}
      <div className={`transition-all duration-300 ${scrolled ? 'bg-[#FAF7F2]/95 backdrop-blur-md shadow-sm py-2.5 border-b border-[#E7E0D2]' : 'bg-[#FAF7F2] py-3.5 border-b border-[#E7E0D2] shadow-xs'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between">
          
          {/* Logo with warm accent subtitle */}
          <Link to="/" className="flex flex-col items-start group focus:outline-none" aria-label="Fodtid Forside">
            <img 
              src="https://assets.auxodev.dk/assets/d4428667-cde9-4c31-9224-88278506a01a/fodtid-logo_94fa7593.png" 
              alt="Fodtid Logo" 
              className="h-12 sm:h-16 md:h-20 lg:h-22 w-auto object-contain transition-transform duration-200 group-hover:scale-102"
            />
          </Link>

          {/* Desktop Navigation (visible on screens >= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center gap-7">
            <NavLink 
              to="/" 
              className={({ isActive }) => 
                `text-xs uppercase tracking-wider font-semibold transition-colors py-2 relative ${
                  isActive ? 'text-brand-charcoal font-bold' : 'text-brand-muted hover:text-brand-charcoal'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  Forside
                  {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-accent rounded-full" />}
                </>
              )}
            </NavLink>

            {/* Ydelser Dropdown */}
            <div className="relative" ref={servicesRef}>
              <button
                type="button"
                onClick={() => {
                  setServicesDropdownOpen(!servicesDropdownOpen);
                  setBlogDropdownOpen(false);
                }}
                className={`flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold transition-colors py-2 focus:outline-none ${
                  location.pathname.startsWith('/ydelser') ? 'text-brand-charcoal font-bold' : 'text-brand-muted hover:text-brand-charcoal'
                }`}
                aria-expanded={servicesDropdownOpen}
              >
                <span>Ydelser</span>
                <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${servicesDropdownOpen ? 'rotate-180 text-brand-accent' : 'text-brand-muted'}`} aria-hidden="true" />
              </button>

              {servicesDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-canvas rounded-2xl shadow-xl border border-brand-border p-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                  <NavLink
                    to="/ydelser"
                    end
                    className={({ isActive }) =>
                      `flex items-start gap-3 p-2.5 rounded-xl text-xs font-medium transition-colors ${
                        isActive ? 'bg-surface text-brand-charcoal font-bold' : 'hover:bg-surface/60 text-brand-charcoal'
                      }`
                    }
                  >
                    <div className="w-8 h-8 rounded-full bg-surface border border-brand-border text-brand-charcoal flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-list-check text-brand-accent" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="font-semibold text-brand-charcoal">Alle Ydelser</div>
                      <div className="text-brand-muted text-[11px]">Samlet oversigt over behandlinger</div>
                    </div>
                  </NavLink>

                  <NavLink
                    to="/ydelser/komplet-fodbehandling"
                    className={({ isActive }) =>
                      `flex items-start gap-3 p-2.5 rounded-xl text-xs font-medium transition-colors ${
                        isActive ? 'bg-surface text-brand-charcoal font-bold' : 'hover:bg-surface/60 text-brand-charcoal'
                      }`
                    }
                  >
                    <div className="w-8 h-8 rounded-full bg-surface border border-brand-border text-brand-charcoal flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-hand-holding-heart text-brand-accent" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="font-semibold text-brand-charcoal">Komplet Fodbehandling</div>
                      <div className="text-brand-muted text-[11px]">Fodbad, neglepleje, hård hud & massage</div>
                    </div>
                  </NavLink>

                  <NavLink
                    to="/ydelser/abonnement"
                    className={({ isActive }) =>
                      `flex items-start gap-3 p-2.5 rounded-xl text-xs font-medium transition-colors ${
                        isActive ? 'bg-surface text-brand-charcoal font-bold' : 'hover:bg-surface/60 text-brand-charcoal'
                      }`
                    }
                  >
                    <div className="w-8 h-8 rounded-full bg-surface border border-brand-border text-brand-charcoal flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-repeat text-brand-accent" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="font-semibold text-brand-charcoal">Fodpleje Abonnement</div>
                      <div className="text-brand-muted text-[11px]">Nemt, trygt og til 119 kr./mdr.</div>
                    </div>
                  </NavLink>

                  <NavLink
                    to="/ydelser/laserbehandling"
                    className={({ isActive }) =>
                      `flex items-start gap-3 p-2.5 rounded-xl text-xs font-medium transition-colors ${
                        isActive ? 'bg-surface text-brand-charcoal font-bold' : 'hover:bg-surface/60 text-brand-charcoal'
                      }`
                    }
                  >
                    <div className="w-8 h-8 rounded-full bg-surface border border-brand-border text-brand-charcoal flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-wand-magic-sparkles text-brand-accent" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="font-semibold text-brand-charcoal">Laserbehandling</div>
                      <div className="text-brand-muted text-[11px]">Hælsporer, ledsmerter & neglesvamp</div>
                    </div>
                  </NavLink>
                </div>
              )}
            </div>

            <NavLink 
              to="/priser" 
              className={({ isActive }) => 
                `text-xs uppercase tracking-wider font-semibold transition-colors py-2 relative ${
                  isActive ? 'text-brand-charcoal font-bold' : 'text-brand-muted hover:text-brand-charcoal'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  Pris & Abonnement
                  {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-accent rounded-full" />}
                </>
              )}
            </NavLink>

            <NavLink 
              to="/produkter" 
              className={({ isActive }) => 
                `text-xs uppercase tracking-wider font-semibold transition-colors py-2 relative ${
                  isActive ? 'text-brand-charcoal font-bold' : 'text-brand-muted hover:text-brand-charcoal'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  Produkter
                  {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-accent rounded-full" />}
                </>
              )}
            </NavLink>

            {/* Blog & Viden Link */}
            <NavLink 
              to="/blog" 
              className={({ isActive }) => 
                `text-xs uppercase tracking-wider font-semibold transition-colors py-2 relative ${
                  isActive ? 'text-brand-charcoal font-bold' : 'text-brand-muted hover:text-brand-charcoal'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  Blog & Viden
                  {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-accent rounded-full" />}
                </>
              )}
            </NavLink>

            <NavLink 
              to="/om-os" 
              className={({ isActive }) => 
                `text-xs uppercase tracking-wider font-semibold transition-colors py-2 relative ${
                  isActive ? 'text-brand-charcoal font-bold' : 'text-brand-muted hover:text-brand-charcoal'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  Om Fodtid
                  {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-accent rounded-full" />}
                </>
              )}
            </NavLink>

            <NavLink 
              to="/kontakt" 
              className={({ isActive }) => 
                `text-xs uppercase tracking-wider font-semibold transition-colors py-2 relative ${
                  isActive ? 'text-brand-charcoal font-bold' : 'text-brand-muted hover:text-brand-charcoal'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  Kontakt
                  {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-accent rounded-full" />}
                </>
              )}
            </NavLink>
          </nav>

          {/* Desktop Right CTA */}
          <div className="hidden min-[1200px]:flex items-center">
            <a
              href={`tel:${phone.replace(/\s+/g, '')}`}
              className="inline-flex items-center justify-center gap-2 bg-[#A67C3D] hover:bg-[#91692E] text-white font-bold px-6 py-2.5 rounded-full text-xs uppercase tracking-wider shadow-sm hover:shadow-md transition-all duration-200 active:scale-95"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span>{phone}</span>
            </a>
          </div>

          {/* Mobile hamburger toggle (only visible below 1200px) */}
          <div className="flex min-[1200px]:hidden items-center gap-3">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-1.5 bg-[#A67C3D] text-white font-bold px-4 py-2 rounded-full text-xs uppercase tracking-wider shadow-sm"
            >
              <i className="fa-solid fa-calendar-check" aria-hidden="true" />
              <span>Book tid</span>
            </Link>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-10 h-10 rounded-full bg-surface border border-brand-border text-brand-charcoal flex items-center justify-center text-base hover:bg-surface-soft focus:outline-none transition-colors"
              aria-label={mobileMenuOpen ? "Luk menu" : "Åbn menu"}
              aria-expanded={mobileMenuOpen}
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'}`} aria-hidden="true" />
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden absolute top-full left-0 right-0 sm:left-auto sm:right-0 sm:w-[420px] h-[calc(100vh-100%)] sm:h-auto sm:max-h-[calc(100dvh-100%)] overflow-y-auto bg-canvas/98 backdrop-blur-xl z-50 p-6 flex flex-col justify-between border-t border-brand-border sm:border-l sm:border-b sm:rounded-bl-3xl shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="space-y-4">
            
            <div className="space-y-1 border-b border-brand-border pb-4">
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-base font-semibold transition-colors ${
                    isActive ? 'bg-surface text-brand-charcoal font-bold' : 'text-brand-muted hover:bg-surface/50'
                  }`
                }
              >
                Forside
              </NavLink>

              <NavLink
                to="/ydelser"
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-base font-semibold transition-colors ${
                    isActive ? 'bg-surface text-brand-charcoal font-bold' : 'text-brand-muted hover:bg-surface/50'
                  }`
                }
              >
                Ydelser & Behandlinger
              </NavLink>

              <div className="pl-6 space-y-1">
                <NavLink
                  to="/ydelser/komplet-fodbehandling"
                  className="block px-3 py-2 text-sm text-brand-muted hover:text-brand-charcoal"
                >
                  • Komplet Fodbehandling (499 kr.)
                </NavLink>
                <NavLink
                  to="/ydelser/abonnement"
                  className="block px-3 py-2 text-sm text-brand-muted hover:text-brand-charcoal"
                >
                  • Fodpleje Abonnement (119 kr./mdr.)
                </NavLink>
                <NavLink
                  to="/ydelser/laserbehandling"
                  className="block px-3 py-2 text-sm text-brand-muted hover:text-brand-charcoal"
                >
                  • Laserbehandling mod smerter & svamp
                </NavLink>
              </div>

              <NavLink
                to="/priser"
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-base font-semibold transition-colors ${
                    isActive ? 'bg-surface text-brand-charcoal font-bold' : 'text-brand-muted hover:bg-surface/50'
                  }`
                }
              >
                Priser & Abonnement
              </NavLink>

              <NavLink
                to="/produkter"
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-base font-semibold transition-colors ${
                    isActive ? 'bg-surface text-brand-charcoal font-bold' : 'text-brand-muted hover:bg-surface/50'
                  }`
                }
              >
                Produkter & Cremer
              </NavLink>

              <NavLink
                to="/blog"
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-base font-semibold transition-colors ${
                    isActive ? 'bg-surface text-brand-charcoal font-bold' : 'text-brand-muted hover:bg-surface/50'
                  }`
                }
              >
                Blog & Videnscenter
              </NavLink>

              <NavLink
                to="/om-os"
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-base font-semibold transition-colors ${
                    isActive ? 'bg-surface text-brand-charcoal font-bold' : 'text-brand-muted hover:bg-surface/50'
                  }`
                }
              >
                Om Fodtid (Klaus Just)
              </NavLink>

              <NavLink
                to="/kontakt"
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-base font-semibold transition-colors ${
                    isActive ? 'bg-surface text-brand-charcoal font-bold' : 'text-brand-muted hover:bg-surface/50'
                  }`
                }
              >
                Kontakt & Bestilling
              </NavLink>
            </div>

            {/* Direct Contact info inside mobile menu */}
            <div className="bg-surface rounded-2xl p-4 border border-brand-border space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-canvas border border-brand-border text-brand-charcoal flex items-center justify-center">
                  <i className="fa-solid fa-phone text-brand-accent" aria-hidden="true" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-brand-muted font-bold">Ring direkte</div>
                  <a href={`tel:${phone.replace(/\s+/g, '')}`} className="text-lg font-bold text-brand-charcoal whitespace-nowrap">
                    {phone}
                  </a>
                </div>
              </div>
              <div className="text-xs text-brand-muted border-t border-brand-border/60 pt-2 flex flex-col gap-1">
                <span>Hjemmebehandlinger:</span>
                <span className="font-semibold text-brand-charcoal">{hours}</span>
              </div>
            </div>

          </div>

          <div className="pt-6">
            <Link
              to="/kontakt"
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-charcoal text-canvas font-bold py-4 rounded-full text-center shadow"
            >
              <i className="fa-regular fa-calendar-check text-brand-accent" aria-hidden="true" />
              <span>Bestil tid online</span>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
